import { assetHasQuotation, hasQuotatedSaleValue } from './quotationAssets';

/** Physical assets allowed for a management; Moneda (no asset_code) is always offered in UI. */
export type ManagementAssetRules = {
  assetCodes: string[];
  hidesPoliticalCreditType: boolean;
};

export const MSG_MISSING_BOL_PRICE =
  'Falta configurar el precio de la bolsa de cemento.';
export const MSG_MISSING_M3H_PRICE =
  'Falta configurar el precio del m³ de hormigón.';
export const MSG_MISSING_USD_QUOTE =
  'Falta configurar la cotización del dólar.';

export const MSG_MISSING_USD_QUOTATION_FOR_CONTRIBUTIONS =
  `${MSG_MISSING_USD_QUOTE} Cargala en Configuración → Cotizaciones → Dólar.`;

export const MANAGEMENT_CHANGE_WARNING =
  'Al cambiar la Gerencia se eliminarán los productos que no sean compatibles con la nueva selección.';

const CREDIT_TYPE_POLITICO = 'Político';

const MANAGEMENT_ASSET_RULES: Record<string, ManagementAssetRules> = {
  GCUY: { assetCodes: ['BOL'], hidesPoliticalCreditType: false },
  GHUY: { assetCodes: ['M3H'], hidesPoliticalCreditType: false },
  GCHUY: { assetCodes: ['BOL', 'M3H'], hidesPoliticalCreditType: false },
  EXUY: { assetCodes: ['BOL'], hidesPoliticalCreditType: true },
  ZFUY: { assetCodes: ['M3H'], hidesPoliticalCreditType: true },
};

/** No rules (Argentina / unknown codes) → null → no restriction. */
export function getManagementAssetRules(
  managementCode?: string | null
): ManagementAssetRules | null {
  if (!managementCode) return null;
  return MANAGEMENT_ASSET_RULES[managementCode] ?? null;
}

export function resolveManagementCode(
  managementId: number | string | null | undefined,
  managements: Array<{ id: number; code: string }>
): string | undefined {
  if (managementId === '' || managementId == null) return undefined;
  const id = Number(managementId);
  if (!Number.isFinite(id)) return undefined;
  return managements.find((m) => m.id === id)?.code;
}

export function isPoliticalCreditTypeName(name?: string | null): boolean {
  return name === CREDIT_TYPE_POLITICO;
}

export function shouldHidePoliticalCreditType(
  rules: ManagementAssetRules | null
): boolean {
  return rules?.hidesPoliticalCreditType === true;
}

/** Moneda (empty asset) always compatible; physical assets must be in rules.assetCodes. */
export function isProductCompatibleWithManagement(
  product: { asset_code?: string | null },
  rules: ManagementAssetRules | null
): boolean {
  if (!rules) return true;
  const code = product.asset_code;
  if (code == null || String(code).trim() === '') return true;
  return rules.assetCodes.includes(String(code));
}

export function filterProductsForManagement<T extends { asset_code?: string | null }>(
  products: T[],
  rules: ManagementAssetRules | null
): T[] {
  if (!rules) return products;
  return products.filter((p) => isProductCompatibleWithManagement(p, rules));
}

/**
 * Modal + purge/recalc only when the *target* management has asset rules (UY map).
 * Argentina codes (GM/GI/GH) → false: keep legacy interaction (no modal, no product mutation).
 */
export function shouldConfirmManagementProductPurge(
  newManagementCode: string | null | undefined,
  hasProducts: boolean
): boolean {
  return getManagementAssetRules(newManagementCode) != null && hasProducts;
}

export function hasOriginationProductsLoaded(
  products?: Array<{
    amount?: { value?: number | string };
    asset_code?: string | null;
    asset_count?: number | string | null;
  }> | null
): boolean {
  if (!products?.length) return false;
  return products.some((p) => {
    const amount = Number(p.amount?.value);
    const count = Number(p.asset_count);
    return (
      (Number.isFinite(amount) && amount > 0) ||
      (p.asset_code != null && String(p.asset_code).trim() !== '') ||
      (Number.isFinite(count) && count > 0)
    );
  });
}

type QuotationAsset = { code: string; saleValue?: unknown };

function hasAssetQuote(assets: QuotationAsset[], code: string): boolean {
  const asset = assets.find((a) => a.code === code);
  return !!asset && assetHasQuotation(asset);
}

/** Base asset quotes required for "Moneda" lines (GCH → BOL). */
function baseAssetCodesForMoneda(rules: ManagementAssetRules): string[] {
  if (rules.assetCodes.includes('BOL') && rules.assetCodes.includes('M3H')) {
    return ['BOL'];
  }
  return [...rules.assetCodes];
}

/**
 * Missing quotation messages for a dropdown option or selected product line.
 * assetCode empty string / undefined = Moneda.
 */
export function getMissingQuotationMessages(params: {
  rules: ManagementAssetRules | null;
  assetCode?: string | null;
  currencyCode?: string | null;
  assets: QuotationAsset[];
  /** When rules is null, only check the selected physical asset has a quote. */
}): string[] {
  const { rules, assetCode, currencyCode, assets } = params;
  const msgs: string[] = [];
  const code = assetCode != null ? String(assetCode).trim() : '';
  const isUsd = currencyCode === 'USD';

  if (!rules) {
    if (code && !hasAssetQuote(assets, code)) {
      if (code === 'BOL') msgs.push(MSG_MISSING_BOL_PRICE);
      else if (code === 'M3H') msgs.push(MSG_MISSING_M3H_PRICE);
    }
    if (isUsd && !hasAssetQuote(assets, 'USD')) {
      msgs.push(MSG_MISSING_USD_QUOTE);
    }
    return msgs;
  }

  if (!code) {
    for (const base of baseAssetCodesForMoneda(rules)) {
      if (!hasAssetQuote(assets, base)) {
        msgs.push(base === 'BOL' ? MSG_MISSING_BOL_PRICE : MSG_MISSING_M3H_PRICE);
      }
    }
  } else if (code === 'BOL') {
    if (!hasAssetQuote(assets, 'BOL')) msgs.push(MSG_MISSING_BOL_PRICE);
  } else if (code === 'M3H') {
    if (!hasAssetQuote(assets, 'M3H')) msgs.push(MSG_MISSING_M3H_PRICE);
    // GCH: M3H → BOL conversion needs BOL quote
    if (
      rules.assetCodes.includes('BOL') &&
      rules.assetCodes.includes('M3H') &&
      !hasAssetQuote(assets, 'BOL')
    ) {
      msgs.push(MSG_MISSING_BOL_PRICE);
    }
  } else if (!rules.assetCodes.includes(code)) {
    // incompatible asset — treat as blocked (no specific quote msg)
  } else if (!hasAssetQuote(assets, code)) {
    if (code === 'BOL') msgs.push(MSG_MISSING_BOL_PRICE);
    else if (code === 'M3H') msgs.push(MSG_MISSING_M3H_PRICE);
  }

  if (isUsd && !hasAssetQuote(assets, 'USD')) {
    msgs.push(MSG_MISSING_USD_QUOTE);
  }

  return [...new Set(msgs)];
}

export function isAssetOptionEnabled(
  rules: ManagementAssetRules | null,
  assetCode: string,
  assets: QuotationAsset[]
): boolean {
  if (rules && assetCode && !rules.assetCodes.includes(assetCode)) {
    return false;
  }
  // Any physical asset without quotation is disabled (legacy AR/origination behavior).
  if (assetCode && !hasAssetQuote(assets, assetCode)) {
    return false;
  }
  return (
    getMissingQuotationMessages({
      rules,
      assetCode,
      currencyCode: null,
      assets,
    }).length === 0
  );
}

/** First allowed physical asset with quotation; undefined → default to Moneda. */
export function pickDefaultAssetCode(
  rules: ManagementAssetRules | null,
  assets: QuotationAsset[]
): string | undefined {
  const codes = rules?.assetCodes ?? ['BOL'];
  for (const code of codes) {
    if (isAssetOptionEnabled(rules, code, assets)) return code;
  }
  return undefined;
}

export function recalculateProductAmountsWithQuotations<
  T extends {
    asset_code?: string | null;
    asset_count?: number | null;
    amount: { currency_code: string; value: number };
  },
>(products: T[], assets: QuotationAsset[]): T[] {
  return products.map((p) => {
    if (!p.asset_code || !p.asset_count) return p;
    const asset = assets.find((a) => a.code === p.asset_code);
    if (!asset || !hasQuotatedSaleValue(asset.saleValue)) return p;
    const unit = asset.saleValue as number;
    return {
      ...p,
      amount: {
        ...p.amount,
        value: unit * p.asset_count,
      },
    };
  });
}

/**
 * Product side-effects of a management change.
 * Without rules (e.g. GM): same array reference — amounts untouched, no purge.
 */
export function applyManagementChangeToProducts<
  T extends {
    asset_code?: string | null;
    asset_count?: number | null;
    amount: { currency_code: string; value: number };
  },
>(
  products: T[],
  newManagementCode: string | null | undefined,
  assets: QuotationAsset[]
): T[] {
  const rules = getManagementAssetRules(newManagementCode);
  if (!rules) return products;
  return recalculateProductAmountsWithQuotations(
    filterProductsForManagement(products, rules),
    assets
  );
}

export function filterCreditTypesByManagement<T extends { name: string }>(
  creditTypes: T[] | undefined | null,
  rules: ManagementAssetRules | null
): T[] {
  if (!creditTypes?.length) return [];
  if (!shouldHidePoliticalCreditType(rules)) return creditTypes;
  return creditTypes.filter((ct) => !isPoliticalCreditTypeName(ct.name));
}
